<!-- frontend/src/routes/WalkList.svelte -->
<script lang="ts">
  import { onMount } from 'svelte';
  import { push } from 'svelte-spa-router';
  import { get } from 'svelte/store';
  import { filters, filtersToParams } from '../lib/filters';
  import { getWalkList, apiFetchAuthed, type WalkListItem } from '../lib/api';
  import { polygonStore } from '../lib/stores/polygon';
  import { groupAndSort, lastContactLabel } from '../lib/walkListSort';
  import { walkListToCsv } from '../lib/walkListCsv';

  // Export so tests can inject items directly (onMount does not fire in jsdom).
  export let items: WalkListItem[] = [];
  // Export loading so tests that inject items can bypass the disabled guard.
  export let loading = true;
  let truncated = false;
  let error: string | null = null;

  let mode: 'list' | 'forms' = 'list';

  interface IssueTag {
    name: string;
    display_name: string;
    sort_order: number;
  }
  let issueTags: IssueTag[] = [];

  onMount(async () => {
    try {
      const [walkResp, tagResp] = await Promise.all([
        getWalkList(filtersToParams(get(filters)), get(polygonStore)),
        apiFetchAuthed('/api/issue_tags').then(r => r.json()),
      ]);
      items = walkResp.items;
      truncated = walkResp.truncated;
      issueTags = (tagResp.items ?? []).slice().sort(
        (a: IssueTag, b: IssueTag) => a.sort_order - b.sort_order,
      );
    } catch (e) {
      error = 'Failed to load walk list.';
      console.error(e);
    } finally {
      loading = false;
    }
  });

  $: groups = groupAndSort(items);

  function fullName(it: WalkListItem): string {
    return [it.first_name, it.last_name].filter(Boolean).join(' ') || '(no name)';
  }

  function exportCsv() {
    const blob = new Blob([walkListToCsv(items)], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'walk-list.csv';
    a.click();
    URL.revokeObjectURL(a.href);
  }

  function printList() {
    mode = 'list';
    setTimeout(() => window.print(), 0);
  }

  function printForms() {
    mode = 'forms';
    setTimeout(() => window.print(), 0);
  }
</script>

<main class="page">
  <header class="bar no-print">
    <button class="btn" on:click={() => push('/map')}>Back to map</button>
    <h1>Walk List</h1>
    <div class="bar-right">
      {#if $polygonStore && $polygonStore.length >= 3}
        <span class="chip lassoed" title="Walk list scoped to the polygon you drew on the map">
          Lassoed area ({$polygonStore.length} vertices)
        </span>
      {/if}
      <span class="count">{items.length} voters</span>
      <button class="btn" on:click={exportCsv} disabled={loading || items.length === 0}>Export CSV</button>
      <button class="btn" on:click={printList} disabled={loading || items.length === 0}>Print list</button>
      <button class="btn btn-primary" data-testid="print-forms" on:click={printForms} disabled={loading || items.length === 0}>Print canvass forms</button>
    </div>
  </header>

  {#if loading}
    <p class="status">Loading…</p>
  {:else if error}
    <p class="status error">{error}</p>
  {:else}
    {#if truncated}
      <p class="warn no-print">
        Showing the first 500 voters. Narrow your filters to print everything.
      </p>
    {/if}

    {#if mode === 'list'}
      {#each groups as [street, rows] (street)}
        <section class="street">
          <h2>{street}</h2>
          <table>
            <thead>
              <tr><th>☐</th><th>Address</th><th>Name</th><th>Age</th><th>Party</th><th>Last contact</th><th>Notes</th></tr>
            </thead>
            <tbody>
              {#each rows as r (r.id)}
                <tr>
                  <td class="cb">☐</td>
                  <td class="addr">{r.address ?? ''}</td>
                  <td>{fullName(r)}</td>
                  <td>{r.age ?? ''}</td>
                  <td>{r.party ?? ''}</td>
                  <td>{lastContactLabel(r.latest_support_level, r.latest_contact_result)}</td>
                  <td class="notes"></td>
                </tr>
              {/each}
            </tbody>
          </table>
        </section>
      {/each}
    {:else}
      <div class="forms-grid">
        {#each items as r (r.id)}
          <div class="cf-card">
            <div class="cf-header">
              <div class="cf-name">
                {fullName(r)}
                {#if r.petition_signed}<span class="tag tag-petition">SIGNED PETITION</span>{/if}
                {#if r.has_voted}<span class="tag tag-voted">VOTED</span>{/if}
                {#if r.do_not_contact}<span class="tag tag-dnc">DO NOT CONTACT</span>{/if}
              </div>
              <div class="cf-callback">☐ Callback: <span class="cf-callback-line"></span></div>
            </div>
            <div class="cf-meta">{r.address ?? ''}{r.age ? ` · Age ${r.age}` : ''}{r.party ? ` · ${r.party}` : ''}</div>
            <div class="cf-row"><b>Method:</b> ☐ Door ☐ Phone ☐ Text ☐ Mail ☐ Event</div>
            <div class="cf-row"><b>Result:</b> ☐ Spoke ☐ Not home ☐ Refused ☐ Moved ☐ Wrong address</div>
            <div class="cf-row"><b>Support:</b> ☐ Strong against ☐ Lean against ☐ Undecided ☐ Lean support ☐ Strong support</div>
            <div class="cf-row">☐ Yard sign  ☐ Volunteer interest  ☐ Donation interest</div>
            <div class="cf-row cf-issues"><b>Issues:</b> {#each issueTags as t}☐ {t.display_name} {/each}</div>
            <div class="cf-row cf-issue-other"><b>Other:</b> ____________</div>
            <div class="cf-notes">
              <b>Notes:</b>
              <div class="cf-line"></div>
              <div class="cf-line"></div>
            </div>
          </div>
        {/each}
      </div>
    {/if}
  {/if}
</main>

<style>
  .page { padding: 1rem 1.25rem; color: var(--ink); background: var(--paper); min-height: 100vh; }
  .bar { display: flex; align-items: center; gap: 1rem; margin-bottom: 1rem; }
  .bar h1 { font-family: var(--font-display); margin: 0; font-size: 1.6rem; }
  .bar-right { margin-left: auto; display: flex; gap: 0.75rem; align-items: center; }
  .count { color: var(--ink-soft); font-size: 0.9rem; }
  .chip {
    display: inline-flex;
    align-items: center;
    padding: 0.2rem 0.5rem;
    border-radius: 999px;
    font-size: 0.78rem;
    background: var(--paper-soft, #ece5d3);
    color: var(--ink, #0b0b0b);
    border: 1px solid var(--rule, #cdb88c);
  }
  .chip.lassoed {
    background: var(--sun-soft, #f5e6b0);
    border-color: var(--leaf-bright, #4a8a3e);
  }
  .btn { padding: 8px 14px; border: 1px solid var(--rule-strong); background: var(--paper); cursor: pointer; font-family: var(--font-body); }
  .btn-primary { background: var(--ink); color: var(--paper); border-color: var(--ink); }
  .status { color: var(--ink-soft); }
  .status.error { color: var(--alert); }
  .warn { padding: 8px 12px; background: var(--sun-soft); border-left: 3px solid var(--sun); margin-bottom: 1rem; }
  .street { break-inside: avoid; margin-bottom: 1.25rem; }
  .street h2 { font-family: var(--font-display); font-size: 1.1rem; margin: 0.5rem 0 0.25rem; border-bottom: 1px solid var(--rule); padding-bottom: 4px; }
  table { width: 100%; border-collapse: collapse; font-size: 0.85rem; }
  th, td { text-align: left; padding: 4px 6px; border-bottom: 1px dotted var(--rule); }
  th { font-weight: 600; font-size: 0.72rem; text-transform: uppercase; letter-spacing: 0.08em; color: var(--ink-soft); }
  .cb { width: 1.2em; font-size: 1.1rem; }
  .addr { white-space: nowrap; }
  .notes { width: 25%; }

  /* Canvass form cards — one full-width card per voter; 4 per page in print. */
  .forms-grid { display: block; }
  .cf-card {
    border: 1px solid var(--rule, #ccc);
    border-radius: 4px;
    padding: 0.6rem 0.75rem;
    margin-bottom: 0.75rem;
    font-size: 0.82rem;
    break-inside: avoid;
  }
  .cf-header {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 0.75rem;
    margin-bottom: 0.2rem;
  }
  .cf-name { font-weight: 700; font-size: 0.98rem; }
  .cf-callback { white-space: nowrap; font-size: 0.82rem; }
  .cf-callback-line {
    display: inline-block;
    width: 1.6in;
    border-bottom: 1px solid var(--ink, #000);
  }
  .cf-meta { color: var(--ink-soft); font-size: 0.8rem; margin-bottom: 0.15rem; }
  .tag {
    display: inline-block;
    padding: 1px 5px;
    border-radius: 3px;
    font-size: 0.68rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    vertical-align: middle;
    margin-left: 0.3rem;
  }
  .tag-petition { background: var(--leaf-soft, #d6ecd2); color: var(--leaf-dark, #2a5e23); border: 1px solid var(--leaf, #4a8a3e); }
  .tag-voted { background: #dbeafe; color: #1e40af; border: 1px solid #1e40af; }
  .tag-dnc { background: var(--alert-soft, #fde8e8); color: var(--alert-dark, #8b1c1c); border: 1px solid var(--alert, #c0392b); }
  .cf-row { margin: 0.2rem 0; line-height: 1.45; }
  .cf-notes { margin-top: 0.3rem; }
  .cf-line {
    border-bottom: 1px solid var(--ink, #000);
    height: 1.05rem;
    margin-top: 0.4rem;
  }

  @media print {
    @page { size: letter; margin: 0.4in; }
    .no-print { display: none !important; }
    .page { padding: 0; }
    .street { break-inside: avoid-page; }
    body { background: white; }
    /* Exactly 4 cards per page: each ~1/4 of the 10.2in printable height,
       with a hard page break after every fourth. */
    .cf-card {
      height: 2.35in;
      box-sizing: border-box;
      overflow: hidden;
      break-inside: avoid;
      margin: 0 0 0.1in;
      font-size: 9.5px;
    }
    .cf-card:nth-of-type(4n) { break-after: page; }
    .cf-name { font-size: 11px; }
    .cf-callback, .cf-meta { font-size: 9px; }
    /* Six checklist rows plus two notes rules do not fit at the screen
       spacing, and the card is a fixed height with overflow: hidden, so the
       surplus would be cut off the paper instead of reflowing. Tighten the
       print-only spacing rather than growing the card: the four-per-page
       geometry above stays exactly as it was. Re-measure with
       frontend/e2e/canvass-form-print-budget.mjs before changing any of
       these. */
    .cf-row { margin: 0.12rem 0; }
    .cf-notes { margin-top: 0.15rem; }
    .cf-line { height: 0.2in; margin-top: 0.09in; }
  }
</style>
